import { useIntl } from 'react-intl'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Typography from 'LuxKit/Typography/Typography'
import InlineIcon from 'components/utils/InlineIcon'
import type { ReactElement } from 'react'
import * as React from 'react'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'

export interface BedbankPropertyCancellationPoliciesViewProps {
  icon?: ReactElement | false
  formattedDate: string | undefined
  showHotelOnly: boolean
  /** Restricted rates refund to credit after the deadline, so they are flexible rather than free. */
  isFlexibleCancellation?: boolean
}

function BedbankPropertyCancellationPoliciesView({
  icon,
  formattedDate,
  showHotelOnly,
  isFlexibleCancellation = false,
}: BedbankPropertyCancellationPoliciesViewProps) {
  const intl = useIntl()
  const cancellationColour = useRebrandSuccessDarkColour()

  const highlightValue = (chunks: React.ReactNode) => <Typography colour={cancellationColour} as="b" wrap="no-wrap">{chunks}</Typography>

  const cancellationLabel = (() => {
    if (isFlexibleCancellation && formattedDate && showHotelOnly) {
      return intl.formatMessage({
        id: 'ffbCb3Dt4F9r8nIuRXFK',
        defaultMessage: '<highlight>Flexible cancellation</highlight> until {date} (hotel only)',
        description: 'Flexible cancellation with deadline date and hotel only qualifier for restricted bedbank rates',
      }, { date: formattedDate, highlight: highlightValue })
    }
    if (isFlexibleCancellation && formattedDate) {
      return intl.formatMessage({
        id: '2GgKbhmleC_NYW2190kt',
        defaultMessage: '<highlight>Flexible cancellation</highlight> until {date}',
        description: 'Flexible cancellation with deadline date for restricted bedbank property cancellation policy',
      }, { date: formattedDate, highlight: highlightValue })
    }
    if (formattedDate && showHotelOnly) {
      return intl.formatMessage({
        id: '_UOXpRkJSI3QsryTl3kK',
        defaultMessage: '<highlight>Free cancellation</highlight> until {date} (hotel only)',
        description: 'Free cancellation with deadline date and hotel only qualifier',
      }, { date: formattedDate, highlight: highlightValue })
    }
    if (formattedDate) {
      return intl.formatMessage({
        id: 'TqR6xjNhrVT73n9iws_1',
        defaultMessage: '<highlight>Free cancellation</highlight> until {date}',
        description: 'Free cancellation with deadline date for bedbank property cancellation policy',
      }, { date: formattedDate, highlight: highlightValue })
    }
    if (showHotelOnly) {
      return intl.formatMessage({
        id: 'JWw7UgoKoUb168_PHEms',
        defaultMessage: '<highlight>Free cancellation</highlight> (hotel only)',
        description: 'Free cancellation with hotel only qualifier in the cancellation flow',
      }, { highlight: highlightValue })
    }
    return intl.formatMessage({
      id: '9wiJtoEELLbKy5maMYCD',
      defaultMessage: '<highlight>Free cancellation</highlight>',
      description: 'Free cancellation text for bedbank property cancellation policy',
    }, { highlight: highlightValue })
  })()

  return <BodyTextBlock startIcon={icon} variant="medium">
    {cancellationLabel}
    <Typography as="span" wrap="no-wrap">
      <InlineIcon Icon={LineInfoCircleIcon} />
    </Typography>
  </BodyTextBlock>
}

export default BedbankPropertyCancellationPoliciesView
